<script lang="ts">
	import { get } from 'svelte/store';
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { isValidURL } from 'src/helpers/utilities';
	import { devices_settings } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
	import { isBluTRV, macToID } from 'src/helpers/interface_helpers';
	import {
		accessibleVirtualDevices,
		createVirtualDevice,
		deleteVirtualDevice,
		getDeviceIDByComponent,
	} from 'src/helpers/virtual_helpers/virtual_components_helpers';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { deleteBluTRV } from 'src/helpers/virtual_helpers/blutrv_helpers';
	import { saveVCSettings } from 'src/helpers/virtual_helpers/virtual_components_settings_helpers';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { postReq } from 'src/services/http';
	//Design system components
	import { Button, Container, Modal, Text, Alert, Icon } from '@Shelly/shelly-design-system';
	//Components
	import VcNameSettings from './VCNameSettings.svelte';
	import VirtualGroupList from './VirtualGroupList.svelte';
	import VcExtractAsDevice from './VCExtractAsDevice.svelte';
	import DeviceInList from 'src/components/Lists/Devices/DeviceInList.svelte';
	//Types
	import type { Device } from 'src/types/Device';
	import type { CloudApiResponse } from 'src/types/responses/CloudApiResponse';
	import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';

	export let device: Device;
	export let component: string;
	export let showModal: boolean;
	export let showCreateTemplateModal: boolean;

	const _device = device;
	const deviceSettings: DeviceSettingsG2 = devices_settings.getByRealId(device.id);
	let settings = deviceSettings?.[component];
	const trvComponent = Object.keys(deviceSettings || {})
		.filter((i) => /^(blutrv):2\d{2}$/.test(i))
		.find((i) => deviceSettings[i]?.trv == component);
	const deviceComponent = trvComponent || component;
	const vcDeviceID = getDeviceIDByComponent(device.id, deviceComponent);
	const bthomeAccountDevice = get(devices)[macToID(settings?.addr)];
	const groupComponent = /^(group):2\d{2}$/.test(component);

	let extractAsDevice = $devices?.[vcDeviceID] ? true : false;
	let virtualGroupList: VirtualGroupList;
	let selectedItems: string[];
	let showDeleteModal = false;
	let waitForSaveResponse = false;
	let waitForDeleteResponse = false;

	const onSave = async () => {
		waitForSaveResponse = true;
		let res: CloudApiResponse;
		const allDevices = get(devices);
		const vcDevice = allDevices[vcDeviceID];
		if (extractAsDevice && !vcDevice) {
			res = await createVirtualDevice(device, settings, deviceComponent);
		} else if (!extractAsDevice && vcDevice) {
			res = isBluTRV(device.type)
				? await deleteBluTRV(vcDevice)
				: await deleteVirtualDevice(vcDevice);
		} else if (extractAsDevice && vcDevice) {
			vcDevice.name = settings.name;
			const response = await postReq(`/interface/device/save`, {
				id: vcDeviceID,
				data: JSON.stringify(vcDevice),
			});
			if (response?.isok) {
				devices.update(vcDevice);
			}
		}
		if (!res || res?.isok) {
			res = await saveVCSettings(device, component.split(':')[0], settings);
			if (res?.isok && virtualGroupList && groupComponent) {
				await virtualGroupList.save(settings.id);
			}
			showModal = false;
		} else {
			onSettingsResponse(res, device);
		}
		waitForSaveResponse = false;
	};

	const onDelete = async () => {
		waitForDeleteResponse = true;
		let res: CloudApiResponse = groupComponent
			? await gen2Request(device, 'Virtual.Delete', {
					key: component,
				})
			: await gen2Request(device, 'Bthome.Deletedevice', {
					id: settings.id,
				});
		if (!res || res?.isok) {
			const vcDevice = get(devices)[vcDeviceID];
			if (vcDevice) {
				await deleteVirtualDevice(vcDevice);
			}
			showDeleteModal = false;
			showModal = false;
		} else {
			onSettingsResponse(res, device);
		}
		waitForDeleteResponse = false;
	};

	$: errorURL =
		settings?.meta?.ui?.icon !== '' &&
		settings?.meta?.ui?.icon != null &&
		!isValidURL(settings?.meta?.ui?.icon);
</script>

{#if settings}
	<Modal
		{showModal}
		on:close={() => (showModal = false)}
		title={groupComponent
			? $format('virtual_group_modal_settings_title', {
					default: 'Virtual Group Settings',
				})
			: $format('virtual_bthome_modal_settings_title', {
					default: 'BTHome Device Settings',
				})}
		removeScrollPadding
	>
		<svelte:fragment slot="content">
			<Container padding="scrollPaddings">
				<VcNameSettings bind:settings label={component} />
				{#if groupComponent}
					<VirtualGroupList
						device={_device}
						{component}
						bind:this={virtualGroupList}
						bind:selectedItems
					/>
				{:else if bthomeAccountDevice}
					<Alert
						type="info"
						class="mb-s"
						heading={$format('alert_heading_inclusion_device_is_already_present_title', {
							default: 'This device is already present in your account.',
						})}
					/>
					<DeviceInList device={bthomeAccountDevice} />
					<div class="mb-s" />
				{/if}
				<VcExtractAsDevice bind:extractAsDevice bind:showModal />
				{#if groupComponent}
					<div class="mb-s d-flex">
						<Button
							size="m"
							variant="secondary"
							disabled={!accessibleVirtualDevices()}
							on:click={() => (showCreateTemplateModal = true)}
							>{$format('vc_template_create_template', {
								default: 'Create Virtual Template Device',
							})}</Button
						>
					</div>
				{/if}
				<Button size="m" class="mb-s" variant="danger" on:click={() => (showDeleteModal = true)}
					>{groupComponent
						? $format('button_delete_group', {
								default: 'Delete Group',
							})
						: $format('button_delete_bthome', {
								default: 'Delete bthome device',
							})}</Button
				>
			</Container>
		</svelte:fragment>
		<div class="d-flex justify-content-end" slot="footer">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => (showModal = false)}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</Button>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				responsiveSize={{
					sm: 'm',
				}}
				disabled={waitForSaveResponse ||
					errorURL ||
					(virtualGroupList && selectedItems?.length == 0)}
				loading={waitForSaveResponse}
				on:click={onSave}
			>
				{$format('button_save', { default: 'Save' })}
			</Button>
		</div>
	</Modal>
	<Modal
		on:close={() => (showDeleteModal = false)}
		showModal={showDeleteModal}
		title={groupComponent
			? `${$format('button_delete', { default: 'Delete' })} ${$format('label_virtual_group', {
					default: 'Virtual Group',
				})}?`
			: `${$format('button_delete_bthome_capital', {
					default: 'Delete BTHome Device',
				})}?`}
	>
		<Container slot="content" padding="0">
			<div class="d-flex flex-direction-column align-items-center">
				<Icon class="mb-xs" icon="text-danger fa-light fa-trash" size="xxl" />
				<Text class="text-bold text-center mb-s" styleType="controls-large">
					{settings?.name || component}
				</Text>
				<Text class="align-self-start">
					{groupComponent
						? $format('on_delete_virtual_group_msg', {
								values: { group_name: settings?.name || component },
								default: `Deleting virtual group ‘{group_name}’ will remove it from your virtual components.`,
							})
						: $format('on_delete_bthome_device_msg', {
								values: { bthome_name: settings?.name || component },
								default: `Are you sure you want to delete bthome device ‘{bthome_name}’?`,
							})}
				</Text>
			</div>
		</Container>
		<div slot="footer" class="d-flex justify-content-end">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => (showDeleteModal = false)}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</Button>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				variant="danger"
				responsiveSize={{
					sm: 'm',
				}}
				loading={waitForDeleteResponse}
				on:click={onDelete}
			>
				{groupComponent
					? `${$format('button_delete', { default: 'Delete' })} ${$format(
							'process_delete_group',
						).toLowerCase()}`
					: `${$format('button_delete', { default: 'Delete' })} ${$format('process_device').toLowerCase()}`}
			</Button>
		</div>
	</Modal>
{/if}
